import React from "react";
import { Box, Button, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useMetricsUi } from "../../components/metrics/metricsUi";
import { ForecastResultSkeleton } from "./forecastUi";

/**
 * Shown in place of a SKU's forecast chart when the product has that SKU
 * (per its catalog variants) but no forecast-data run has ever been stored
 * for it — the SKU chip still appears, but selecting it lands here instead
 * of a blank/missing tab. Lets the user generate one on the spot.
 *
 * Purely presentational — `status`/`errorMessage` are owned by the parent
 * (ForecastResults), not local state, specifically so an in-flight
 * generation survives the user switching to another SKU tab and back: this
 * component unmounts/remounts with every tab change, but the parent doesn't,
 * so the "generating" skeleton (or a finished result, or an error) is still
 * correct whenever they return to this SKU rather than resetting to "idle".
 */
const SkuOnDemandPanel = ({
  sku,
  label,
  status = "idle", // "idle" | "generating" | "error"
  errorMessage = "",
  onGenerate,
}) => {
  const displayName = label || sku;
  const ui = useMetricsUi();
  const { colors } = ui;

  if (status === "generating") {
    return (
      <ForecastResultSkeleton message={`Generating forecast for ${displayName}…`} />
    );
  }

  return (
    <Box
      sx={{
        ...ui.card,
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
        No demand found with the given parameters
      </Typography>
      <Typography
        sx={{ fontSize: 12.5, color: colors.textSecondary, maxWidth: 420 }}
      >
        {status === "error"
          ? errorMessage
          : `No current forecast for ${displayName} — data may be missing or out of date.`}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={status === "error" ? <RefreshIcon /> : <AutoAwesomeIcon />}
        onClick={onGenerate}
      >
        {status === "error" ? "Retry" : `Generate forecast for ${displayName}`}
      </Button>
    </Box>
  );
};

export default SkuOnDemandPanel;
